import React, { useState, useEffect, useMemo } from 'react'
import { isEmpty, prop, sortBy, filter, compose, map } from 'ramda'
import {
  getTextByLang
} from '../../ExternalFields'
import { useStore } from '../../../StateProvider'
import { useTranslation } from 'react-i18next'
import {
  getExternalFieldsList,
  getIsExtFldsValid,
  removeQuestionnaireSteps,
  sortExtFlds
} from '../tierFormUtils'
import { BlockWithSteps } from '../BlockWithSteps/BlockWithSteps'
import { Button, Form } from '../../../ui'
import styles from '../style.css'

/**
 * @param {number} tier
 */
const getStepsByTier = (tier) => compose(sortBy(prop('id')), filter(step => step.tier === tier))

const TierFormWithSteps = ({ isLoadingSubmit, onSubmit, buttonName }) => {
  const [{
    whitelabel: { extFields, steps, customTexts, questionnaireTier },
    validation, validation: { tier },
    form,
    form: { accId },
    user: { questionnairePassed }
  }] = useStore()
  const [fieldsDescriptionBySteps, setFieldsDescriptionBySteps] = useState([])
  const [fieldsBySteps, setFieldsBySteps] = useState([])
  const [currentStep, setCurrentStep] = useState(1)
  const [isStepValid, setIsStepValid] = useState(false)
  const { t } = useTranslation()
  const { alreadyExistsDiffError, alreadyExistsSameError } = form[accId]

  const tierSteps = useMemo(() => {
    const allSteps = getStepsByTier(tier)(steps)
    return questionnairePassed ? removeQuestionnaireSteps(extFields, allSteps, tier) : allSteps
  }, [steps, tier, extFields])

  useEffect(() => {
    if (isEmpty(fieldsDescriptionBySteps) || !tier) return
    setIsStepValid(getIsExtFldsValid(validation[`tier${tier}`][accId], fieldsDescriptionBySteps[currentStep - 1]))
  }, [currentStep, validation, fieldsDescriptionBySteps])

  useEffect(() => {
    if (isEmpty(tierSteps)) return
    if (alreadyExistsDiffError || alreadyExistsSameError) {
      const emailDescription = extFields.find((fld) => fld.name === 'email')
      if (emailDescription?.step) {
        setCurrentStep(emailDescription.step)
      }
    }
  }, [alreadyExistsDiffError, alreadyExistsSameError, extFields, tierSteps])

  useEffect(() => {
    if (!tier) return
    /**
     * @type {ExternalField[]}
     */
    let fldDescriptions = tierSteps.map((step) => extFields.filter(
      fldDescription => (fldDescription.tier === tier && fldDescription.step === step.id)))
    if (questionnaireTier === tier && questionnairePassed) {
      fldDescriptions = compose(
        filter((fldsInStep) => !isEmpty(fldsInStep)),
        map((fldsInStep) => fldsInStep.filter(
          fldDescription => (fldDescription.type !== 'questionnaireSelect')))
      )(fldDescriptions)
    }
    setFieldsDescriptionBySteps(fldDescriptions)
    /**
     * @type {(Object[])[]}
     */
    const flds = fldDescriptions.map(
      (fldDescriptionsInStep) => sortExtFlds(getExternalFieldsList(fldDescriptionsInStep))
    )
    setFieldsBySteps(flds)
  }, [extFields, tier, tierSteps])

  const onNextButtonClick = () => {
    if (currentStep < tierSteps.length) {
      setCurrentStep(index => index + 1)
      window.scrollTo(0, 0)
    } else {
      onFormSubmit()
    }
  }

  const onPrevButtonClick = () => {
    setCurrentStep(step => step - 1)
  }

  const onFormSubmit = () => {
    onSubmit().catch(() => {})
  }

  return fieldsBySteps.length ? (
    <>
      <Form>
        <BlockWithSteps
          currentStep={currentStep - 1}
          tierSteps={tierSteps}
          fieldsBySteps={fieldsBySteps}
        />
        <div className={styles.buttons}>
          {currentStep > 1 && (
            <Button
              style='back'
              onClick={onPrevButtonClick}
              disabled={isLoadingSubmit}
            >
              {t('jointAcc.toPrevStep')}
            </Button>)}
          <Button
            style='next'
            onClick={onNextButtonClick}
            disabled={!isStepValid || isLoadingSubmit}
            isLoading={isLoadingSubmit}
          >
            {currentStep < tierSteps.length ? t('createAccountForm.continue') : buttonName}
          </Button>
        </div>
      </Form>
      <div className={styles.additionalText}>
        {getTextByLang('additionalConditions', customTexts)}
      </div>
    </>
  ) : null
}

export { TierFormWithSteps }
